/**
 * `+ Add ▸ Board` (spec M1-B1 §7.5).
 *
 * Two tabs, **From scratch** and **Templates**. From scratch: a required name (≤ 255), the
 * workspace (defaulting to the current one), an optional folder, the board-type radio —
 * *Main (`public`) / Shareable (`share`) / Private (`private`)*, each with its one-line
 * explanation — an optional description and the item terminology.
 *
 * The UI string and the stored value are **never** confused: monday says Main/Shareable/
 * Private and stores `public`/`share`/`private` `[fl]`. Creating navigates to the new board's
 * default view.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useToast, useOverlayDismiss } from '@elizmonday/ui';
import { useTRPC } from '../../lib/trpc';
import { TemplatePicker } from '../templates';
import { boardUrl } from '../shell/urls';
import {
  BOARD_KIND_HINT,
  BOARD_KIND_LABEL,
  ITEM_TERMINOLOGY_PRESETS,
  pluralise,
  useBoardMutations,
  type BoardKind,
} from './hooks';

const KINDS: BoardKind[] = ['public', 'share', 'private'];

/** The `Templates` tab: a count and a button that opens the real picker. */
function TemplateTab({
  workspaceId,
  folderId,
  onClose,
}: {
  workspaceId: string | null;
  folderId: string | null;
  onClose(): void;
}) {
  const trpc = useTRPC();
  const templates = useQuery({ ...trpc.templates.list.queryOptions({}), staleTime: 30_000 });
  const [picking, setPicking] = useState(false);
  if (picking) {
    return <TemplatePicker workspaceId={workspaceId} folderId={folderId} onClose={onClose} />;
  }
  return (
    <div className="ez-board-dialog__hint" data-testid="create-templates-tab">
      <p>
        {templates.data === undefined
          ? 'Loading templates…'
          : templates.data.length === 0
            ? 'No templates yet. Save a board as a template from its ··· menu.'
            : `${templates.data.length} template${templates.data.length === 1 ? '' : 's'} available.`}
      </p>
      <button
        type="button"
        data-testid="create-templates-open"
        disabled={(templates.data ?? []).length === 0}
        onClick={() => setPicking(true)}
      >
        Start with template
      </button>
    </div>
  );
}

export function CreateBoardDialog({
  workspaceId,
  folderId,
  onClose,
}: {
  workspaceId: string | null;
  folderId?: string | null;
  onClose(): void;
}) {
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });

  const mutations = useBoardMutations();
  const navigate = useNavigate();
  const toast = useToast();

  const [tab, setTab] = useState<'scratch' | 'templates'>('scratch');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<BoardKind>('public');
  const [description, setDescription] = useState('');
  const [terminology, setTerminology] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('A board needs a name');
      return;
    }
    if (trimmed.length > 255) {
      setError('A board name is at most 255 characters');
      return;
    }
    void (async () => {
      const board = await mutations.create.mutateAsync({
        name: trimmed,
        kind,
        ...(workspaceId ? { workspaceId } : {}),
        ...(folderId ? { folderId } : {}),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
        ...(terminology === ''
          ? {}
          : { itemTerminology: terminology, itemTerminologyPlural: pluralise(terminology) }),
      });
      onClose();
      toast(`${board.name} created`, { kind: 'positive' });
      // §7.5: create navigates to the new board's default view.
      navigate(`${boardUrl(String(board.id))}/views/${String(board.viewId)}`);
    })();
  };

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Create board"
        data-testid="create-board-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <h2 className="ez-board-dialog__title">Create board</h2>

        <div className="ez-board-dialog__tabs" role="tablist" aria-label="Create board from">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'scratch'}
            data-testid="create-tab-scratch"
            onClick={() => setTab('scratch')}
          >
            From scratch
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'templates'}
            data-testid="create-tab-templates"
            onClick={() => setTab('templates')}
          >
            Templates
          </button>
        </div>

        {tab === 'templates' ? (
          /**
           * B9. monday reaches templates from `+ Add ▸ Board ▸ Start with template`, not from a
           * tab inside this dialog — but §3.3 reserved the tab and a link is cheaper than
           * removing it, so both muscle memories work.
           */
          <TemplateTab
            workspaceId={workspaceId}
            folderId={folderId ?? null}
            onClose={onClose}
          />
        ) : (
          <>
            <label className="ez-board-dialog__field">
              Board name
              <input
                data-testid="create-board-name"
                value={name}
                maxLength={255}
                // The dialog exists to be typed into; focus belongs in its first field.
                autoFocus
                onChange={(event) => {
                  setName(event.target.value);
                  setError(null);
                }}
              />
            </label>

            <fieldset className="ez-board-dialog__radios">
              <legend>Board type</legend>
              {KINDS.map((value) => (
                <label key={value} className="ez-board-dialog__radio">
                  <input
                    type="radio"
                    name="board-kind"
                    value={value}
                    checked={kind === value}
                    data-testid={`create-board-kind-${value}`}
                    onChange={() => setKind(value)}
                  />
                  <span>
                    <strong>{BOARD_KIND_LABEL[value]}</strong>
                    <em>{BOARD_KIND_HINT[value]}</em>
                  </span>
                </label>
              ))}
            </fieldset>

            <label className="ez-board-dialog__field">
              Description
              <textarea
                data-testid="create-board-description"
                value={description}
                maxLength={4000}
                rows={2}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>

            <label className="ez-board-dialog__field">
              One row is called
              <input
                list="ez-terminology-presets"
                data-testid="create-board-terminology"
                value={terminology}
                maxLength={64}
                placeholder="Item"
                onChange={(event) => setTerminology(event.target.value)}
              />
              <datalist id="ez-terminology-presets">
                {ITEM_TERMINOLOGY_PRESETS.map((preset) => (
                  <option key={preset} value={preset} />
                ))}
              </datalist>
            </label>
          </>
        )}

        {error ? (
          <p className="ez-board-dialog__error" role="alert" data-testid="create-board-error">
            {error}
          </p>
        ) : null}

        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="ez-board-dialog__save"
            data-testid="create-board-submit"
            disabled={tab === 'templates' || mutations.create.isPending}
          >
            Create board
          </button>
        </footer>
      </form>
    </div>
  );
}
